import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Chip, IconButton, Tooltip } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  MoreTime as ExtendIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DemoAccountService } from '../../services/demoAccountService';
import type { DemoAccount, CreateDemoAccountResponse } from '../../types/demoAccount';
import { CreateDemoAccountDialog } from './CreateDemoAccountDialog';
import { CredentialsSummaryDialog } from './CredentialsSummaryDialog';
import { ExtendExpiryDialog } from './ExtendExpiryDialog';
import ConfirmationPopover from '../../components/ConfirmationPopover/ConfirmationPopover';
import { useConfirmationPopover } from '../../hooks/useConfirmationPopover';
import { ToastService } from '../../services/toastService';

function getExpiryChip(account: DemoAccount) {
  if (account.isDisabled) {
    return <Chip label="Disabled" size="small" color="default" />;
  }
  if (account.isExpired) {
    return <Chip label="Expired" size="small" color="error" />;
  }
  if (account.demoExpiresAt) {
    const expiresAt = new Date(account.demoExpiresAt);
    const now = new Date();
    const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 7) {
      return <Chip label={`Expiring (${daysLeft}d)`} size="small" color="warning" />;
    }
    return <Chip label={`Active (${daysLeft}d)`} size="small" color="success" />;
  }
  return <Chip label="Active" size="small" color="success" />;
}

export const DemoAccountsPage: React.FC = () => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [credentialsResult, setCredentialsResult] = useState<CreateDemoAccountResponse | null>(
    null
  );
  const [extendAccount, setExtendAccount] = useState<DemoAccount | null>(null);
  const [accountToDelete, setAccountToDelete] = useState<DemoAccount | null>(null);

  const { anchorEl, isOpen, openConfirmation, closeConfirmation } = useConfirmationPopover();

  const queryClient = useQueryClient();

  const {
    data: demoAccountsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['demoAccounts'],
    queryFn: DemoAccountService.getDemoAccounts,
  });

  useEffect(() => {
    if (error) {
      ToastService.handleApiError(error, 'Loading Demo Accounts');
    }
  }, [error]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => DemoAccountService.deleteDemoAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demoAccounts'] });
      ToastService.success('Demo account deleted successfully', 'Account Deleted');
      closeConfirmation();
      setAccountToDelete(null);
    },
    onError: (error: unknown) => {
      ToastService.handleApiError(error, 'Delete Demo Account');
    },
  });

  const extendMutation = useMutation({
    mutationFn: ({ id, additionalDays }: { id: string; additionalDays: number }) =>
      DemoAccountService.extendDemoAccount(id, additionalDays),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demoAccounts'] });
      ToastService.success('Demo account extended successfully', 'Account Extended');
      setExtendAccount(null);
    },
    onError: (error: unknown) => {
      ToastService.handleApiError(error, 'Extend Demo Account');
    },
  });

  const handleCreateSuccess = (result: CreateDemoAccountResponse) => {
    queryClient.invalidateQueries({ queryKey: ['demoAccounts'] });
    setCreateDialogOpen(false);
    setCredentialsResult(result);
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLElement>, account: DemoAccount) => {
    setAccountToDelete(account);
    openConfirmation(event.currentTarget);
  };

  const handleConfirmDelete = () => {
    if (accountToDelete) {
      deleteMutation.mutate(accountToDelete.id);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'displayName',
      headerName: 'Display Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.2,
      minWidth: 200,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value === 'fleetManager' ? 'Fleet Manager' : 'Trader'}
          size="small"
          color={params.value === 'fleetManager' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'entityName',
      headerName: 'Entity',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'vesselCount',
      headerName: 'Vessels',
      width: 80,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'expiryStatus',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => getExpiryChip(params.row),
    },
    {
      field: 'demoExpiresAt',
      headerName: 'Expires',
      width: 120,
      valueFormatter: (value: string) => {
        if (!value) return 'Never';
        return new Date(value).toLocaleDateString();
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      valueFormatter: (value: string) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString();
      },
    },
    {
      field: 'lastSignIn',
      headerName: 'Last Sign In',
      width: 130,
      valueFormatter: (value: string) => {
        if (!value) return 'Never';
        return new Date(value).toLocaleDateString();
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Extend expiry">
            <IconButton size="small" onClick={() => setExtendAccount(params.row)}>
              <ExtendIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete demo account">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => handleDeleteClick(e, params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const accounts = demoAccountsData?.demoAccounts || [];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Demo Accounts
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Demo Account
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%' }}>
        <DataGrid
          rows={accounts}
          columns={columns}
          getRowId={(row) => row.id}
          loading={isLoading}
          autoHeight
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
        />
      </Paper>

      <CreateDemoAccountDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <CredentialsSummaryDialog
        open={credentialsResult !== null}
        onClose={() => setCredentialsResult(null)}
        result={credentialsResult}
      />

      <ExtendExpiryDialog
        open={extendAccount !== null}
        onClose={() => setExtendAccount(null)}
        account={extendAccount}
        onExtend={(days) => {
          if (extendAccount) {
            extendMutation.mutate({
              id: extendAccount.id,
              additionalDays: days,
            });
          }
        }}
        isLoading={extendMutation.isPending}
      />

      <ConfirmationPopover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => {
          closeConfirmation();
          setAccountToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Demo Account"
        message={`This will permanently delete the user, entity, and all seeded vessel data for "${accountToDelete?.displayName}". This cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteMutation.isPending}
      />
    </Box>
  );
};
